import { Controller } from "@hotwired/stimulus"
import { template } from "lodash"

export default class extends Controller {
  static targets = [
    "successMessageTemplate",
    "bankAccountTemplate",
    "bankAccountsContainer",
    "bankAccountCard",
    "changesContainer",
    "paginationContainer"
  ]

  static values = {
    departmentId: String,
    existingAccounts: Array,
    totalCount: Number,
    currentPage: Number,
    totalPages: Number
  }

  connect() {
    super.connect()

    this.isNewDepartment = this.departmentIdValue === 'new' || this.departmentIdValue === '' || !this.departmentIdValue
    this.bankAccountChanges = {}
    this.currentPageValue = 1

    if (this.hasBankAccountTemplateTarget) {
      this.bankAccountTemplate = template(this.bankAccountTemplateTarget.innerHTML)
    }

    if (this.hasSuccessMessageTemplateTarget) {
      this.successMessageTemplate = template(this.successMessageTemplateTarget.innerHTML)
    }

    this.renderExistingAccounts()

    window.addEventListener("bank-account:selected", this.selectAccount.bind(this))
    window.addEventListener("bank-account:create", this.selectAccount.bind(this))
  }

  renderExistingAccounts() {
    if (!this.bankAccountTemplate || !this.hasExistingAccountsValue) {
      return
    }

    this.existingAccountsValue.forEach(accountData => {
      this.addBankAccountToExistingList(accountData, { skipTracking: true })
    })
  }

  removeBankAccount(event) {
    const { bankAccountVersionId } = event.params

    if (!bankAccountVersionId) {
      return
    }

    const bankAccountCard = this.bankAccountCardTargets.find(
      card => card.dataset.bankAccountVersionId === bankAccountVersionId.toString()
    )

    if (bankAccountCard) {
      if (!this.isNewDepartment) {
        this.trackChange(bankAccountVersionId, { _delete: '1' })
        bankAccountCard.style.display = 'none'
      } else {
        bankAccountCard.remove()
      }
    }
  }

  updateMainAccountInForm(checked, bankAccountVersionId) {
    if (!checked) return

    const allRadios = this.element.querySelectorAll('[data-main-bank-account-radio]')
    allRadios.forEach(radio => {
      radio.checked = false
    })

    const selectedRadio = this.element.querySelector(`[data-main-bank-account-radio][value="${bankAccountVersionId}"]`)
    if (selectedRadio) {
      selectedRadio.checked = true
    }

    this.bankAccountCardTargets.forEach(card => {
      const badge = card.querySelector("[data-main-badge]")
      if (badge) badge.classList.add("hidden")
    })
    const selectedCard = this.bankAccountCardTargets.find(
      c => c.dataset.bankAccountVersionId === bankAccountVersionId.toString()
    )
    const mainBadge = selectedCard?.querySelector("[data-main-badge]")
    if (mainBadge) mainBadge.classList.remove("hidden")
  }

  updateMainAccount(event) {
    const radio = event.currentTarget
    const bankAccountVersionId = radio.value

    const card = radio.closest('[data-department--bank-accounts-target="bankAccountCard"]')
    if (card) {
      const showOnInvoiceCheckbox = card.querySelector('input[type="checkbox"][name="show_on_invoice_bank_account_version_ids[]"]')
      if (showOnInvoiceCheckbox && !showOnInvoiceCheckbox.checked) {
        showOnInvoiceCheckbox.checked = true
        showOnInvoiceCheckbox.dispatchEvent(new Event("change"))
      }
    }

    if (!this.isNewDepartment) {
      // Wyczyść poprzednie main — także konta z serwera które nigdy nie były trackowane
      const currentMain = this.element.querySelector('[data-main-bank-account-radio]:checked')
      if (currentMain && currentMain.value !== bankAccountVersionId.toString()) {
        this.trackChange(currentMain.value, { main_on_department: '0' })
      }
      Object.keys(this.bankAccountChanges).forEach(key => {
        if (this.bankAccountChanges[key].main_on_department === '1') {
          this.bankAccountChanges[key].main_on_department = '0'
        }
      })
      this.trackChange(bankAccountVersionId, { main_on_department: '1', show_on_invoice: '1' })
    }

    this.updateMainAccountInForm(radio.checked, bankAccountVersionId)
  }

  updateShowOnInvoice(event) {
    const checkbox = event.currentTarget
    const bankAccountVersionId = checkbox.dataset.bankAccountVersionId

    const card = checkbox.closest('[data-department--bank-accounts-target="bankAccountCard"]')
    const showBadge = card?.querySelector("[data-show-badge]")
    if (showBadge) showBadge.classList.toggle("hidden", !checkbox.checked)

    if (!this.isNewDepartment) {
      this.trackChange(bankAccountVersionId, { show_on_invoice: checkbox.checked ? '1' : '0' })
    }
  }

  selectAccount(event) {
    const bankAccount = event.detail.bank_account
    if (bankAccount) {
      this.addBankAccountToExistingList(bankAccount)
      this.dispatch("selected", { bubbles: true, target: window })
    } else { console.error("Error selected account") }
  }

  addBankAccountToExistingList(accountData, { skipTracking = false } = {}) {
    if (!this.bankAccountTemplate || !this.hasBankAccountsContainerTarget) {
      return
    }

    const versionId = (accountData.id || '').toString()

    // Deduplikacja: sprawdź czy karta już jest w DOM
    const existingCard = this.bankAccountCardTargets.find(
      c => c.dataset.bankAccountVersionId === versionId
    )
    if (existingCard && !skipTracking) {
      // Karta istnieje — jeśli była usunięta (diff _delete), cofnij usunięcie
      if (this.bankAccountChanges[versionId] && this.bankAccountChanges[versionId]._delete) {
        delete this.bankAccountChanges[versionId]
        this.rebuildChangeInputs()
        existingCard.style.display = ''
      }
      // Scroll do karty + highlight
      existingCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      existingCard.style.outline = '2px solid #337ab7'
      setTimeout(() => { existingCard.style.outline = '' }, 2000)
      return
    }

    const templateData = {
      id: accountData.id,
      bank_account_id: accountData.bank_account ? accountData.bank_account.id : accountData.bank_account_id,
      name: accountData.bank_account ? accountData.bank_account.name : accountData.name,
      bank_name: accountData.bank_name,
      bank_account_number: accountData.bank_account_number,
      bank_swift: accountData.bank_swift || "",
      bank_currency: accountData.bank_currency,
      account_description: accountData.account_description || "",
      own_bank_account: accountData.own_bank_account || false,
      factor_bank_account: accountData.factor_bank_account || false,
      main_on_department: accountData.main_on_department || false,
      show_on_invoice: accountData.show_on_invoice || false,
      department_id: this.departmentIdValue
    }

    const cardHtml = this.bankAccountTemplate(templateData)
    this.bankAccountsContainerTarget.insertAdjacentHTML('beforeend', cardHtml)

    const card = this.bankAccountCardTargets.find(
      c => c.dataset.bankAccountVersionId === templateData.id.toString()
    )
    if (!card) return

    // Dla istniejących działów: usuń hidden input bank_account_version_ids (diff flow)
    if (!this.isNewDepartment) {
      const hiddenInput = card.querySelector('input[name="bank_account_version_ids[]"]')
      if (hiddenInput) hiddenInput.remove()

      // Śledź dodanie jako zmianę + zachowaj dane karty do odtworzenia po paginacji
      if (!skipTracking) {
        this.trackChange(templateData.id, { _new: true })
        this.bankAccountChanges[templateData.id.toString()]._cardData = accountData
      }
    }

    if (accountData.main_on_department) {
      const radioButton = card.querySelector('[data-main-bank-account-radio]')
      if (radioButton) {
        radioButton.checked = true
      }
    }

    if (accountData.show_on_invoice) {
      const checkbox = card.querySelector('[data-show-on-invoice-checkbox]')
      if (checkbox) checkbox.checked = true
    }
  }

  showSuccessMessage(message) {
    if (!this.successMessageTemplate) {
      return
    }

    const messageHtml = this.successMessageTemplate({ message })

    this.element.insertAdjacentHTML('beforeend', messageHtml)
    const messageElement = this.element.lastElementChild

    setTimeout(() => messageElement.remove(), 3000)
  }

  showErrorMessage(message) {
    alert(message)
  }

  // --- Diff-based tracking ---

  trackChange(versionId, attributes) {
    versionId = versionId.toString()
    if (attributes._delete && this.bankAccountChanges[versionId] && this.bankAccountChanges[versionId]._new) {
      // Usunięcie konta które dopiero co dodaliśmy - anulujemy oba
      delete this.bankAccountChanges[versionId]
    } else {
      this.bankAccountChanges[versionId] = Object.assign(
        this.bankAccountChanges[versionId] || { bank_account_version_id: versionId },
        attributes
      )
    }
    this.rebuildChangeInputs()
  }

  rebuildChangeInputs() {
    let container = this.hasChangesContainerTarget
      ? this.changesContainerTarget
      : this.element.querySelector('[data-changes-container]')

    if (!container) {
      container = document.createElement('div')
      container.setAttribute('data-changes-container', '')
      container.setAttribute('data-department-bank-account-select-target', 'changesContainer')
      this.element.insertBefore(container, this.element.firstChild)
    }

    container.innerHTML = ''
    let index = 0
    Object.keys(this.bankAccountChanges).forEach(key => {
      const change = this.bankAccountChanges[key]
      const prefix = `department[bank_accounts][${index}]`
      Object.keys(change).forEach(attr => {
        if (attr === '_new') return // flaga wewnętrzna, nie wysyłamy
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = `${prefix}[${attr}]`
        input.value = change[attr]
        container.appendChild(input)
      })
      index++
    })

    // Wyczyść nazwy starych inputów, żeby nie były wysyłane razem z diff flow
    // Radio zachowuje prefixowany name żeby grouping HTML działał poprawnie
    this.element.querySelectorAll('input[name="main_bank_account_version_id"]').forEach(el => { el.name = '_main_bank_account_version_id' })
    this.element.querySelectorAll('input[name="show_on_invoice_bank_account_version_ids[]"]').forEach(el => { el.name = '' })
    this.element.querySelectorAll('input[name="bank_account_version_ids[]"]').forEach(el => { el.name = '' })
  }

  async loadPage(event) {
    if (this.isNewDepartment) return

    const direction = event.currentTarget.dataset.direction
    let newPage = this.currentPageValue

    if (direction === 'next' && this.currentPageValue < this.totalPagesValue) {
      newPage++
    } else if (direction === 'prev' && this.currentPageValue > 1) {
      newPage--
    } else {
      return
    }

    try {
      const response = await fetch(
        `/departments/${this.departmentIdValue}/bank_accounts/attached?page=${newPage}`,
        {
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        }
      )
      const data = await response.json()

      this.currentPageValue = data.page
      this.totalPagesValue = data.total_pages
      this.totalCountValue = data.total_count

      // Usuń istniejące karty
      this.bankAccountCardTargets.forEach(card => card.remove())

      // Renderuj nowe z reconciliacją trackowanych zmian
      data.bank_accounts.forEach(accountData => {
        const vid = accountData.id.toString()
        const change = this.bankAccountChanges[vid]

        // Reconcile: nadpisz dane z serwera trackowanymi zmianami
        if (change) {
          if (change._delete) return // pomiń usunięte
          if (change.main_on_department !== undefined) {
            accountData.main_on_department = change.main_on_department === '1'
          }
          if (change.show_on_invoice !== undefined) {
            accountData.show_on_invoice = change.show_on_invoice === '1'
          }
        }

        this.addBankAccountToExistingList(accountData, { skipTracking: true })
      })

      // Odtwórz nowo dodane (niezapisane) karty
      Object.keys(this.bankAccountChanges).forEach(vid => {
        if (this.bankAccountChanges[vid]._new && this.bankAccountChanges[vid]._cardData) {
          this.addBankAccountToExistingList(this.bankAccountChanges[vid]._cardData, { skipTracking: true })
        }
      })

      // Wyczyść name na nowo wyrenderowanych kartach
      this.rebuildChangeInputs()

      // Aktualizuj paginację
      this.updatePaginationControls()
    } catch (error) {
      console.error('Błąd ładowania strony kont bankowych:', error)
    }
  }

  updatePaginationControls() {
    if (!this.hasPaginationContainerTarget) return

    const indicator = this.paginationContainerTarget.querySelector('[data-page-indicator]')
    const prevBtn = this.paginationContainerTarget.querySelector('[data-direction="prev"]')
    const nextBtn = this.paginationContainerTarget.querySelector('[data-direction="next"]')

    if (indicator) indicator.textContent = `${this.currentPageValue} / ${this.totalPagesValue}`
    if (prevBtn) prevBtn.disabled = this.currentPageValue <= 1
    if (nextBtn) nextBtn.disabled = this.currentPageValue >= this.totalPagesValue
  }
}
